import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { certificatService } from '../../services/certificatService';
import clientService from '../../services/clientService'; // Import the client service
import Card from '../common/Card.jsx';
import Pagination from '../common/Pagination.jsx';
import CertificatList from './Certificats/CertificatList';
import AddCertificatModal from './Certificats/AddCertificatModal';
import EditCertificatModal from './Certificats/EditCertificatModal';
import ViewCertificatModal from './Certificats/ViewCertificatModal';
import DeleteCertificatModal from './Certificats/DeleteCertificatModal';
import { useTheme } from '../../contexts/ThemeContext'; // Add this import
import ClientSelectDropdown from '../common/ClientSelectDropdown'; // Import the new ClientSelectDropdown component

const Certificats = () => {
  const { user } = useAuth();
  const [certificats, setCertificats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    maxPage: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    searchTerms: '',
    clientId: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCertificat, setSelectedCertificat] = useState(null);

  // Mobile menu state
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const { isDarkMode } = useTheme(); // Add this line

  // Load certificats from API with filters
  const loadCertificats = async (pageSize = pagination.pageSize, page = pagination.page, filterParams = filters) => {
    setLoading(true);
    setError('');
    
    try {
      // Prepare filter parameters for API
      const apiFilters = {};
      if (filterParams.searchTerms?.trim()) apiFilters.searchTerms = filterParams.searchTerms.trim();
      if (filterParams.clientId) apiFilters.clientId = filterParams.clientId;

      const result = await certificatService.getCertificats(pageSize, page, apiFilters);
      
      if (result.success) {
        setCertificats(result.data.list);
        setPagination({
          page: result.data.page,
          pageSize: result.data.pageSize,
          total: result.data.total,
          maxPage: result.data.maxPage
        });
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Erreur lors du chargement des certificats');
    } finally {
      setLoading(false);
    }
  };

  // Export functionality - using existing table data only
  const handleExport = (format = 'csv') => {
    if (!certificats || certificats.length === 0) {
      setError('Aucune donnée à exporter');
      return;
    }

    setExporting(true);
    
    try {
      if (format === 'csv') {
        exportToCSV(certificats);
      } else if (format === 'excel') {
        exportToExcel(certificats);
      } else if (format === 'json') {
        exportToJSON(certificats);
      }
    } catch (error) {
      console.error('Export error:', error);
      setError('Erreur lors de l\'exportation des données');
    } finally {
      setExporting(false);
    }
  };

  // Export to CSV
  const exportToCSV = (data) => {
    const headers = [
      'ID',
      'Nom du Certificat',
      'Client',
      'Date de Certification',
      'Chemin du Fichier'
    ];

    const csvContent = [
      headers.join(','),
      ...data.map(certificat => [
        certificat.id || '',
        `"${(certificat.pieceName || '').replace(/"/g, '""')}"`,
        `"${(certificat.raisonSocial || '').replace(/"/g, '""')}"`,
        certificat.dateCertif ? new Date(certificat.dateCertif).toLocaleDateString() : '',
        `"${(certificat.piecePath || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(csvContent, `certificats-export-${timestamp}.csv`, 'text/csv;charset=utf-8;');
  };

  // Export to JSON
  const exportToJSON = (data) => {
    const exportData = {
      exportDate: new Date().toISOString(),
      totalRecords: data.length,
      pagination: {
        currentPage: pagination.page,
        pageSize: pagination.pageSize,
        totalPages: pagination.maxPage,
        totalRecords: pagination.total
      },
      filters: filtersApplied ? filters : null,
      data: data
    };
    
    const jsonContent = JSON.stringify(exportData, null, 2);
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(jsonContent, `certificats-export-${timestamp}.json`, 'application/json');
  };

  // Export to Excel
  const exportToExcel = (data) => {
    const headers = [
      'ID',
      'Nom du Certificat',
      'Client',
      'Date de Certification',
      'Chemin du Fichier'
    ];

    // Excel-compatible CSV with BOM for proper encoding
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join('\t'),
      ...data.map(certificat => [
        certificat.id || '',
        (certificat.pieceName || '').replace(/\t/g, ' '),
        (certificat.raisonSocial || '').replace(/\t/g, ' '),
        certificat.dateCertif ? new Date(certificat.dateCertif).toLocaleDateString() : '',
        (certificat.piecePath || '').replace(/\t/g, ' ')
      ].join('\t'))
    ].join('\n');

    const timestamp = new Date().toISOString().slice(0, 10);
    downloadFile(csvContent, `certificats-export-${timestamp}.xlsx`, 'application/vnd.ms-excel');
  };

  // Download file utility
  const downloadFile = (content, filename, mimeType) => {
    try {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setTimeout(() => {
        console.log(`Export réussi: ${filename}`);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      setError('Erreur lors du téléchargement du fichier');
    }
  };

  // Load certificats on component mount
  useEffect(() => {
    loadCertificats();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      setClientsLoading(true);
      try {
        const result = await clientService.getClients(1000, 1, {});
        if (result.success && Array.isArray(result.data.list)) {
          setClients(result.data.list);
        } else {
          setClients([]);
        }
      } catch (e) {
        setClients([]);
      } finally {
        setClientsLoading(false);
      }
    };
    fetchClients();
  }, []);

  // Show a full right-content loading indicator while API responds
  if (loading) {
    return (
      <div className="p-4 sm:p-6 min-h-screen flex items-start justify-center">
        <div className="w-full max-w-6xl">
          <div className={`rounded-2xl shadow-sm border p-8 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minHeight: '60vh' }}>
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`}></div>
            <div className={`mt-4 text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Chargement des certificats...</div>
            <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Veuillez patienter pendant la récupération des données.</div>
          </div>
        </div>
      </div>
    );
  }

  // Load all clients for filter dropdown
  

  // Handle page change
  const handlePageChange = (newPage) => {
    loadCertificats(pagination.pageSize, newPage, filters);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    loadCertificats(newPageSize, 1, filters);
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setFiltersApplied(Object.values(filters).some(value => value.toString().trim() !== ''));
    loadCertificats(pagination.pageSize, 1, filters);
    setShowFilters(false);
  };

  // Clear filters
  const clearFilters = () => {
    const clearedFilters = {
      searchTerms: '',
      clientId: ''
    };
    setFilters(clearedFilters);
    setFiltersApplied(false);
    loadCertificats(pagination.pageSize, 1, clearedFilters);
    setShowFilters(false);
  };

  // Modal handlers
  const handleView = (certificat) => {
    setSelectedCertificat(certificat);
    setShowViewModal(true);
  };

  const handleEdit = (certificat) => {
    setSelectedCertificat(certificat);
    setShowEditModal(true);
  };

  const handleDelete = (certificat) => {
    setSelectedCertificat(certificat);
    setShowDeleteModal(true);
  };

  const handleRefresh = () => {
    loadCertificats(pagination.pageSize, pagination.page, filters);
  };

  const handleDownload = async (certificat) => {
  if (!certificat.piecePath) {
    setError('Aucun fichier disponible pour téléchargement');
    return;
  }

  try {
    let fileUrl = certificat.piecePath;
    if (!/^https?:\/\//i.test(fileUrl)) {
      fileUrl = `https://images.tendresse.tn/generalsecurite/${fileUrl}`;
    }

    // Fetch the file as a blob
    const response = await fetch(fileUrl, { mode: 'cors' });
    if (!response.ok) throw new Error('Fichier introuvable ou inaccessible');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = certificat.pieceName || 'certificat.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL
    window.URL.revokeObjectURL(url);
  } catch (error) {
    setError('Erreur lors du téléchargement du certificat');
  }
};


  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className={`text-2xl sm:text-3xl font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Certificats</h1>
          <p className={`text-sm sm:text-base mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Gestion des certificats de conformité et rapports de visite
            {filtersApplied && (
              <span className="text-primary-600 font-medium"> (Filtré)</span>
            )}
          </p>
        </div>
        
        {/* Desktop Actions */}
        <div className="hidden lg:flex space-x-3 flex-shrink-0">
          <button 
            onClick={handleRefresh}
            disabled={loading}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className={`fas fa-sync-alt mr-2 ${loading ? 'animate-spin' : ''}`}></i>
            Actualiser
          </button>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`border px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              filtersApplied 
                ? 'bg-primary-50 border-primary-300 text-primary-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="fas fa-filter mr-2"></i>
            Filtrer
            {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
          </button>
          

          
          {user?.isAdmin && (
            <button 
              onClick={() => setShowAddModal(true)}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium"
            >
              <i className="fas fa-plus mr-2"></i>
              Nouveau Certificat
            </button>
          )}
        </div>

        {/* Mobile Actions */}
        <div className="lg:hidden">
          <div className="flex items-center justify-end space-x-2">
            {user?.isAdmin && (
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium flex-1 sm:flex-none"
              >
                <i className="fas fa-plus mr-1 sm:mr-2"></i>
                <span className="hidden xs:inline">Nouveau </span>Certificat
              </button>
            )}
            
            <div className="relative">
              <button 
                onClick={() => setShowMobileActions(!showMobileActions)}
                className="bg-white border border-gray-300 text-gray-700 p-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i className="fas fa-ellipsis-v"></i>
              </button>
              
              {showMobileActions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-2">
                    <button 
                      onClick={() => {
                        handleRefresh();
                        setShowMobileActions(false);
                      }}
                      disabled={loading}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className={`fas fa-sync-alt mr-2 w-4 ${loading ? 'animate-spin' : ''}`}></i>
                      Actualiser
                    </button>
                    <button 
                      onClick={() => {
                        setShowFilters(!showFilters);
                        setShowMobileActions(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        filtersApplied 
                          ? 'text-primary-700 bg-primary-50' 
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <i className="fas fa-filter mr-2 w-4"></i>
                      Filtrer
                      {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
                    </button>
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    <div className="px-4 py-1 text-xs text-gray-500">
                      Exporter {certificats.length} certificat{certificats.length > 1 ? 's' : ''}
                    </div>
                    <button 
                      onClick={() => {
                        handleExport('csv');
                        setShowMobileActions(false);
                      }}
                      disabled={exporting || certificats.length === 0}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className="fas fa-file-csv mr-2 w-4 text-green-600"></i>
                      {exporting ? 'Export...' : 'CSV'}
                    </button>
                    <button 
                      onClick={() => {
                        handleExport('excel');
                        setShowMobileActions(false);
                      }}
                      disabled={exporting || certificats.length === 0}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      <i className="fas fa-file-excel mr-2 w-4 text-green-700"></i>
                      {exporting ? 'Export...' : 'Excel'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`${isDarkMode ? 'bg-gray-900 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'} px-4 py-3 sm:px-6 sm:py-4 rounded-lg`}>
          <div className="flex items-start">
            <i className="fas fa-exclamation-circle mr-2 mt-0.5 flex-shrink-0"></i>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Export Progress */}
      {exporting && (
        <div className={`${isDarkMode ? 'bg-gray-900 border border-blue-700 text-blue-300' : 'bg-blue-50 border border-blue-200 text-blue-700'} px-4 py-3 sm:px-6 sm:py-4 rounded-lg`}>
          <div className="flex items-center">
            <i className="fas fa-download mr-2 animate-pulse"></i>
            <span className="text-sm sm:text-base">Préparation du fichier en cours...</span>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className={`${isDarkMode ? 'border-l-4 border-l-primary-700 bg-gray-800' : 'border-l-4 border-l-primary-500'}`}>
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div className="flex items-center mb-2 sm:mb-0">
                <div className={`${isDarkMode ? 'p-2 bg-gray-700' : 'p-2 bg-primary-100'} rounded-lg mr-3`}>
                  <i className={`fas fa-filter ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}></i>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Filtres de recherche</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Affinez votre recherche de certificats</p>
                </div>
              </div>
              <button
                onClick={() => setShowFilters(false)}
                className={`self-end sm:self-auto ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} p-1`}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <i className="fas fa-search mr-2 text-gray-400"></i>
                  Recherche par nom ou client
                </label>
                <input
                  type="text"
                  value={filters.searchTerms}
                  onChange={(e) => handleFilterChange('searchTerms', e.target.value)}
                  placeholder="Nom du certificat, client..."
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors text-base ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>
              {user?.isAdmin && (
                <div className="space-y-2">
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <i className="fas fa-user mr-2 text-gray-400"></i>
                    Client
                  </label>
                  <ClientSelectDropdown
                    value={filters.clientId}
                    onChange={val => handleFilterChange('clientId', val)}
                    options={clients}
                    loading={clientsLoading}
                    disabled={clientsLoading}
                    isDarkMode={isDarkMode}
                    placeholder="Tous les clients"
                    required={false}
                  />
                </div>
              )}
            </div>

            {/* Filter Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={clearFilters}
                className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium order-2 sm:order-1 ${isDarkMode ? 'text-gray-200 bg-gray-800 hover:bg-gray-700' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
              >
                <i className="fas fa-times mr-2"></i>
                Effacer
              </button>
              <button
                onClick={applyFilters}
                className={`px-6 py-2 rounded-lg transition-colors text-sm font-medium order-1 sm:order-2 ${isDarkMode ? 'bg-primary-700 text-white hover:bg-primary-800' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
              >
                <i className="fas fa-search mr-2"></i>
                Appliquer les filtres
              </button>
            </div>

            {/* Active Filters Summary */}
            {filtersApplied && (
              <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Filtres actifs:</span>
                  {filters.searchTerms && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-primary-900 text-primary-200' : 'bg-primary-100 text-primary-800'}`}>
                      <i className="fas fa-search mr-1"></i>
                      {filters.searchTerms}
                      <button
                        onClick={() => handleFilterChange('searchTerms', '')}
                        className={`${isDarkMode ? 'ml-1 text-primary-400 hover:text-primary-200' : 'ml-1 text-primary-600 hover:text-primary-800'}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                  {filters.clientId && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-primary-900 text-primary-200' : 'bg-primary-100 text-primary-800'}`}>
                      <i className="fas fa-user mr-1"></i>
                      Client #{filters.clientId}
                      <button
                        onClick={() => handleFilterChange('clientId', '')}
                        className={`${isDarkMode ? 'ml-1 text-primary-400 hover:text-primary-200' : 'ml-1 text-primary-600 hover:text-primary-800'}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Certificats List */}
      <CertificatList
        certificats={certificats}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDownload={handleDownload}
      />

      {/* Modals */}
      <AddCertificatModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          handleRefresh();
        }}
      />

      <EditCertificatModal
        isOpen={showEditModal}
        certificat={selectedCertificat}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCertificat(null);
        }}
        onSuccess={() => {
          setShowEditModal(false);
          setSelectedCertificat(null);
          handleRefresh();
        }}
      />

      <ViewCertificatModal
        isOpen={showViewModal}
        certificat={selectedCertificat}
        onClose={() => {
          setShowViewModal(false);
          setSelectedCertificat(null);
        }}
        onDownload={handleDownload}
      />

      <DeleteCertificatModal
        isOpen={showDeleteModal}
        certificat={selectedCertificat}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCertificat(null);
        }}
        onSuccess={() => {
          setShowDeleteModal(false);
          setSelectedCertificat(null);
          handleRefresh();
        }}
      />

      {/* Click outside to close mobile actions */}
      {showMobileActions && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMobileActions(false)}
        />
      )}
    </div>
  );
};

export default Certificats;